import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegClock, FaShuffle } from "react-icons/fa6";
import { IoMdSchool } from "react-icons/io";
import { TiTick } from "react-icons/ti";
import { ImCross } from "react-icons/im";
import { LuCalendarDays } from "react-icons/lu";
import { FaChalkboardTeacher, FaFemale, FaMale } from "react-icons/fa";
import styles from "./studentattendence.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";

const StudentAttendanceDashboard = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";

  const [attendanceData, setAttendanceData] = useState({
    total_student_count: 0,
    total_male_student_count: 0,
    total_female_student_count: 0,
    total_present: 0,
    total_male_present: 0,
    total_female_present: 0,
    total_absent: 0,
    total_male_absent: 0,
    total_female_absent: 0,
    total_leave: 0,
    total_male_leave: 0,
    total_female_leave: 0,
    class: [],
  });

  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const refreshTimerRef = useRef(null);
  const timeTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // Format date for display
  const getFormattedDateTime = useCallback((date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;

    return `${day}/${month}/${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  }, []);

  // Update clock every second
  useEffect(() => {
    timeTimerRef.current = setInterval(() => {
      if (isMountedRef.current) {
        setCurrentTime(new Date());
      }
    }, 1000);

    return () => {
      if (timeTimerRef.current) {
        clearInterval(timeTimerRef.current);
      }
    };
  }, []);

  // Fetch attendance data
  const fetchAttendanceData = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoading(true);
    try {
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];

      const response = await AxiosInstance.post(
        "/attendance/attendancedashboard",
        {
          school_id: parseInt(school_id),
          date: formattedDate,
        },
      );

      if (isMountedRef.current && response.data?.status && response.data.data) {
        const responseData = response.data.data;

        setAttendanceData({
          total_student_count: responseData.total_student_count || 0,
          total_male_student_count: responseData.total_male_student_count || 0,
          total_female_student_count:
            responseData.total_female_student_count || 0,
          total_present: responseData.total_present || 0,
          total_male_present: responseData.total_male_present || 0,
          total_female_present: responseData.total_female_present || 0,
          total_absent: responseData.total_absent || 0,
          total_male_absent: responseData.total_male_absent || 0,
          total_female_absent: responseData.total_female_absent || 0,
          total_leave: responseData.total_leave || 0,
          total_male_leave: responseData.total_male_leave || 0,
          total_female_leave: responseData.total_female_leave || 0,
          class: Array.isArray(responseData.class) ? responseData.class : [],
        });
      }
    } catch (error) {
      console.error("Failed to fetch attendance data:", error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [school_id]);

  // Initial fetch + 30s refresh interval
  useEffect(() => {
    isMountedRef.current = true;

    fetchAttendanceData();

    refreshTimerRef.current = setInterval(() => {
      fetchAttendanceData();
    }, 30000);

    return () => {
      isMountedRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [fetchAttendanceData]);

  const handleClassClick = (classId) => {
    navigate("/user/Student-Attendance-Dashboard-List", {
      state: { classId: classId },
    });
  };

  const StatCard = ({
    title,
    total,
    male,
    female,
    borderColor,
    icon,
    type,
  }) => (
    <div className="col-md-3 mb-4 px-2">
      <div
        className={`card h-100 ${styles.statCard} ${styles[`${type}Card`]}`}
        style={{ borderTop: `6px solid ${borderColor}` }}
      >
        <div className="card-body">
          <div className="d-flex justify-content-center align-items-center gap-2 mb-3">
            <h6 className={`text-uppercase fw-bold mb-0 ${styles.cardTitle}`}>
              {title}
            </h6>
            <span className={styles.cardIcon}>{icon}</span>
          </div>
        </div>

        <div className="card-body">
          <div className="row align-items-center text-center">
            <div className="col-5">
              <h2 className={`fw-bold mb-0 ${styles.totalNumber}`}>{total}</h2>
            </div>
            <div className="col-7">
              <div className={styles.genderStats}>
                <span className={styles.maleStat}>
                  <FaMale size={16} />
                  <span className="ms-1">{male}</span>
                </span>
                <span className={styles.divider}>|</span>
                <span className={styles.femaleStat}>
                  <FaFemale size={16} />
                  <span className="ms-1">{female}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ClassCard = ({ classItem }) => (
    <div
      className={styles.classBox}
      onClick={() => handleClassClick(classItem.class_id)}
    >
      <div className={styles.classInfo}>
        <FaChalkboardTeacher size={20} />
        <strong>
          {translations?.labels?.classLabel} {classItem.class_name}
        </strong>
      </div>
      <div className={styles.classStats}>
        <span className={styles.enrolled}>
          <IoMdSchool size={18} />
          {classItem.class_total_count}
        </span>
        <span className={styles.present}>
          <TiTick size={18} />
          {classItem.present_count}
        </span>
        <span className={styles.absent}>
          <ImCross size={16} />
          {classItem.absent_count}
        </span>
        <span className={styles.leave}>
          <LuCalendarDays size={16} />
          {classItem.leave_count}
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div
          className={`d-flex justify-content-center align-items-center ${styles.loadingContainer}`}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              {translations?.labels?.loading || "Loading..."}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`container-fluid py-4 ${styles.dashboard}`}>
      {/* Header */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-4">
          <h2 className={`fw-bold mb-0 ${styles.mainTitle}`}>
            {translations?.labels?.studentAttendanceDashboard ||
              "Student Attendance Dashboard"}
          </h2>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center gap-3">
          <p className={`text-muted mb-0 ${styles.dateTime}`}>
            {getFormattedDateTime(currentTime)}
          </p>
          <button
            className={`btn btn-sm d-flex align-items-center gap-2 ${styles.switchButton}`}
            onClick={() => navigate("/user/Staff-Attendance-Dashboard")}
          >
            <FaShuffle size={18} />
            <span>
              {translations?.labels?.switchToStaffDashboard ||
                "Switch to Staff Dashboard"}
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row">
        <StatCard
          title={translations?.labels?.enrolled || "Enrolled"}
          total={attendanceData.total_student_count}
          male={attendanceData.total_male_student_count}
          female={attendanceData.total_female_student_count}
          borderColor="#0d6efd"
          type="blue"
          icon={<IoMdSchool color="#0d6efd" size={24} />}
        />

        <StatCard
          title={translations?.labels?.present || "Present"}
          total={attendanceData.total_present}
          male={attendanceData.total_male_present}
          female={attendanceData.total_female_present}
          borderColor="#198754"
          type="green"
          icon={<TiTick color="#198754" size={24} />}
        />

        <StatCard
          title={translations?.labels?.absent || "Absent"}
          total={attendanceData.total_absent}
          male={attendanceData.total_male_absent}
          female={attendanceData.total_female_absent}
          borderColor="#dc3545"
          type="red"
          icon={<ImCross color="#dc3545" size={20} />}
        />

        <StatCard
          title={translations?.labels?.leave || "On Leave"}
          total={attendanceData.total_leave}
          male={attendanceData.total_male_leave}
          female={attendanceData.total_female_leave}
          borderColor="#ffc107"
          type="amber"
          icon={<FaRegClock color="#f59e0b" size={24} />}
        />
      </div>

      {/* Class Cards Grid */}
      <div className={styles.classGrid}>
        {attendanceData.class.map((classItem) => (
          <ClassCard key={classItem.class_id} classItem={classItem} />
        ))}
      </div>
    </div>
  );
};

export default StudentAttendanceDashboard;
